// «Мой пул» — the admin's working screen. Tiles + alert strips + the table.
// Managers additionally see the owner column; layout agreed with Tim 02.08.
import { useEffect, useMemo, useState } from 'preact/hooks';
import {
  api,
  ApiError,
  type VistaAddContributorsRes as AddResult,
  type VistaStaffMe as Me,
  type VistaPoolRow as PoolRow,
  type VistaSummaryRes as Summary,
} from '../api';
import type { T } from '../i18n';
import { PaceCard } from '../components/PaceCard';
import { LiveNow } from '../components/LiveNow';

/** Domain errors of the rate cascade, translated once for every call site. */
export const rateErrorText = (t: T, e: unknown): string | null => {
  if (!(e instanceof ApiError)) return null;
  if (e.code === 'no_admin_rate') return t('pool.err.noAdminRate');
  const max =
    typeof e.body?.maxCents === 'number' ? ` $${(e.body.maxCents / 100).toFixed(2)}/${t('pool.hours')}` : '';
  if (e.code === 'rate_above_admin') return t('pool.err.aboveAdmin', { max });
  if (e.code === 'rate_below_pool') return t('pool.err.belowPool', { max });
  return null;
};

const fmtH = (h: number): string => h.toFixed(h >= 100 ? 0 : 1);
const fmtMoney = (cents: number | null): string =>
  cents == null ? '—' : `$${(cents / 100).toFixed(2)}`;
const ago = (t: T, ms: number | null): string => {
  if (!ms) return '—';
  const d = Math.floor((Date.now() - ms) / 86_400_000);
  if (d <= 0) return t('pool.ago.today');
  if (d === 1) return t('pool.ago.yesterday');
  return t('pool.ago.days', { n: d });
};

// F7 — filters survive navigation (Tim: sifting through 100+ people again on
// every tab switch was the actual complaint). q/statusFilter live here; the
// owner filter stays primarily URL-driven (ManagePage links with ?owner=, and
// "показать всех" clears the hash) — localStorage only supplies the INITIAL
// value when the page is opened with no ?owner= param at all.
const POOL_FILTERS_KEY = 'vista.poolFilters';
type PoolFilters = { q: string; status: string; owner: string };
const loadPoolFilters = (): PoolFilters => {
  try {
    const raw = localStorage.getItem(POOL_FILTERS_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return {
      q: typeof parsed?.q === 'string' ? parsed.q : '',
      status: typeof parsed?.status === 'string' ? parsed.status : '',
      owner: typeof parsed?.owner === 'string' ? parsed.owner : '',
    };
  } catch {
    return { q: '', status: '', owner: '' };
  }
};

export function PoolPage({ me, t }: { me: Me; t: T }) {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [rows, setRows] = useState<PoolRow[] | null>(null);
  const initialFilters = useMemo(loadPoolFilters, []);
  const [q, setQ] = useState(initialFilters.q);
  const [statusFilter, setStatusFilter] = useState<string>(initialFilters.status);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [rateErr, setRateErr] = useState<string | null>(null);
  // The manager screen links here with #/?owner=<name> to show one admin's pool.
  // No ?owner= in the URL at all (a plain #/) falls back to the last saved
  // choice; an EXPLICIT #/ from "показать всех" below still wins because it
  // sets the state directly rather than round-tripping through this default.
  const [ownerFilter, setOwnerFilter] = useState<string>(() => {
    const fromUrl = new URLSearchParams(location.hash.split('?')[1] ?? '').get('owner');
    return fromUrl ?? initialFilters.owner;
  });

  // Persist on every change, including the owner filter — so a later plain
  // reload keeps landing on the last pool actually being worked, not the
  // full unfiltered list.
  useEffect(() => {
    localStorage.setItem(POOL_FILTERS_KEY, JSON.stringify({ q, status: statusFilter, owner: ownerFilter }));
  }, [q, statusFilter, ownerFilter]);

  const load = () => {
    api.summary().then(setSummary).catch(() => {});
    api.pool().then((r) => setRows(r.rows)).catch(() => setRows([]));
  };
  useEffect(load, []);

  // Tab clicks change the hash without remounting this page — the filter must
  // follow the URL, not survive it.
  useEffect(() => {
    const on = () =>
      setOwnerFilter(new URLSearchParams(location.hash.split('?')[1] ?? '').get('owner') ?? '');
    addEventListener('hashchange', on);
    return () => removeEventListener('hashchange', on);
  }, []);

  const view = useMemo(() => {
    if (!rows) return null;
    const needle = q.trim().toLowerCase();
    return rows.filter(
      (r) =>
        (!needle || r.email.includes(needle) || (r.name ?? '').toLowerCase().includes(needle)) &&
        (!statusFilter || r.status === statusFilter) &&
        // By id, not name: two admins can share a name, their pools must not merge.
        (!ownerFilter || r.ownerId === ownerFilter),
    );
  }, [rows, q, statusFilter, ownerFilter]);

  // Distinct owners for the manager's owner dropdown, off the UNFILTERED rows
  // so picking a status/search combo never makes an admin disappear from the
  // list of admins to pick from.
  const owners = useMemo(() => {
    const seen = new Map<string, string>();
    for (const r of rows ?? []) {
      if (r.ownerId && !seen.has(r.ownerId)) seen.set(r.ownerId, r.ownerName ?? r.ownerId);
    }
    return [...seen.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [rows]);

  // Inline rate edit: blur or Enter commits, empty clears. The backend PATCH is
  // scoped, so a viewer gets a 403 and the cell simply reverts on reload.
  const saveRate = async (row: PoolRow, raw: string) => {
    const dollars = raw.trim() === '' ? null : Number(raw);
    if (dollars !== null && (!Number.isFinite(dollars) || dollars < 0)) return;
    const rateCents = dollars === null ? null : Math.round(dollars * 100);
    if (rateCents === row.rateCents) return;
    try {
      await api.patchContributor(row.id, { rateCents });
      setRateErr(null);
      setRows((rs) => rs?.map((r) => (r.id === row.id ? { ...r, rateCents } : r)) ?? null);
    } catch (e) {
      setRateErr(rateErrorText(t, e));
      load();
    }
  };

  return (
    <>
      <div style={{ marginBottom: '14px' }}>
        <PaceCard me={me} t={t} />
      </div>

      <div style={{ marginBottom: '14px' }}>
        <LiveNow me={me} t={t} />
      </div>

      {summary && (
        <>
          {/* Плитки-ссылки: цифра без пути к строкам, которые её составили —
              это тупик. Каждая ведёт в свою стопку очереди. «Снимают» и
              «доход» ссылками не делаем: у них нет одной стопки-источника. */}
          <div class="tiles">
            <a class="tile" href="#/queue/accepted">
              <div class="n">{fmtH(summary.acceptedHours)} {t('pool.hours')}</div>
              <div class="t">{t('pool.tile.accepted')}</div>
            </a>
            <a class="tile" href="#/queue/pending">
              <div class="n">{fmtH(summary.pendingHours)} {t('pool.hours')}</div>
              <div class="t">{t('pool.tile.pending')}</div>
            </a>
            <a class="tile" href="#/queue/rejected">
              <div class="n">{fmtH(summary.rejectedHours)} {t('pool.hours')}</div>
              <div class="t">{t('pool.tile.rejected')}</div>
            </a>
            {summary.clientCents != null && (
              <div class="tile">
                <div class="n">{fmtMoney(summary.clientCents)}</div>
                <div class="t">{t('pool.tile.revenue')}</div>
              </div>
            )}
            <div class="tile">
              <div class="n">{summary.active}/{summary.people}</div>
              <div class="t">{t('pool.tile.filming')}</div>
            </div>
          </div>
          {/* Two queues, two different people to nudge: material waiting for our
              own dubbing, and dubbed material waiting to be sent to the client.
              Nothing expires on a timer here — but either queue standing still is
              filmed work nobody is paid for yet. */}
          {summary.toDub.files > 0 && (
            <a class="strip warn strip-link" href="#/queue/needs_voiceover">
              {t('pool.strip.toDub', { hours: fmtH(summary.toDub.hours), files: summary.toDub.files })}
            </a>
          )}
          {summary.toShip.files > 0 && (
            <a class="strip info strip-link" href="#/queue/voiced">
              {t('pool.strip.toShip', { hours: fmtH(summary.toShip.hours), files: summary.toShip.files })}
            </a>
          )}
        </>
      )}

      {rateErr && <div class="strip bad">{rateErr}</div>}
      {ownerFilter && (
        <div class="strip info">
          {t('pool.filtered')} <b>{rows?.find((r) => r.ownerId === ownerFilter)?.ownerName ?? '…'}</b>{' '}
          <button
            class="link"
            onClick={() => {
              setOwnerFilter('');
              location.hash = '#/';
            }}
          >
            {t('pool.showAll')}
          </button>
        </div>
      )}

      <div class="toolbar">
        <button class="btn-primary" onClick={() => setDialogOpen(true)}>{t('pool.add')}</button>
        <input
          type="search"
          placeholder={t('pool.search')}
          value={q}
          onInput={(e) => setQ((e.target as HTMLInputElement).value)}
        />
        <select value={statusFilter} onChange={(e) => setStatusFilter((e.target as HTMLSelectElement).value)}>
          <option value="">{t('pool.status.all')}</option>
          <option value="invited">{t('pool.status.invited')}</option>
          <option value="joined">{t('pool.status.joined')}</option>
          <option value="active">{t('pool.status.active')}</option>
          <option value="blocked">{t('pool.status.blocked')}</option>
        </select>
        {/* Менеджер видит все пулы — тот же переход, что и клик по строке
            «Админы и пулы» в /manage, но выбираемый прямо здесь. */}
        {me.role === 'manager' && owners.length > 0 && (
          <select
            value={ownerFilter}
            onChange={(e) => {
              const v = (e.target as HTMLSelectElement).value;
              location.hash = v ? `#/?owner=${encodeURIComponent(v)}` : '#/';
            }}
          >
            <option value="">{t('pool.owner.all')}</option>
            {owners.map((o) => (
              <option value={o.id} key={o.id}>{o.name}</option>
            ))}
          </select>
        )}
      </div>

      <div class="tablewrap">
        {view == null ? (
          <div class="empty">{t('pool.loading')}</div>
        ) : view.length === 0 ? (
          <div class="empty">
            {rows?.length === 0 ? t('pool.empty') : t('pool.noMatch')}
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>{t('pool.th.contributor')}</th>
                <th>{t('pool.th.status')}</th>
                <th>{t('pool.th.antifraud')}</th>
                {me.role === 'manager' && <th>{t('pool.th.owner')}</th>}
                <th>{t('pool.th.accepted')}</th>
                <th>{t('pool.th.pending')}</th>
                <th>{t('pool.th.rejected')}</th>
                <th>{t('pool.th.quality')}</th>
                <th>{t('pool.th.rate')}</th>
                <th>{t('pool.th.earned')}</th>
                <th>{t('pool.th.activity')}</th>
                {me.role === 'manager' && me.showBalance && <th>{t('pool.th.revenue')}</th>}
              </tr>
            </thead>
            <tbody>
              {view.map((r) => (
                <tr
                  class={r.status === 'blocked' ? 'row row-blocked' : 'row'}
                  key={r.id}
                  onClick={() => (location.hash = `#/contributor/${r.id}`)}
                >
                  <td>
                    {r.email}
                    {r.name && <span class="sub">{r.name}</span>}
                  </td>
                  <td>
                    <span class={`status-chip ${r.status}`}>{t(`pool.status.${r.status}`)}</span>
                  </td>

                  {me.role === 'manager' && <td>{r.ownerName ?? <span class="muted">—</span>}</td>}
                  <td>{fmtH(r.acceptedHours)}</td>
                  <td>{r.pendingHours > 0 ? <span class="status-chip pend">{fmtH(r.pendingHours)}</span> : '0'}</td>
                  <td>
                    {fmtH(r.rejectedHours)}
                    {r.filmedHours > 0 && (
                      <span class="sub">{Math.round((r.rejectedHours / r.filmedHours) * 100)}%</span>
                    )}
                  </td>
                  <td>{fmtH(r.filmedHours)}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <input
                      class="rate-input"
                      type="number"
                      min="0"
                      step="0.5"
                      value={r.rateCents == null ? '' : (r.rateCents / 100).toString()}
                      placeholder="—"
                      onChange={(e) => saveRate(r, (e.target as HTMLInputElement).value)}
                    />
                  </td>
                  {/* Earned = the admin's own rate promise to this person. Shown
                      only when a rate is set — a guessed default would look like
                      an obligation we never made. */}
                  <td>{r.earnedCents == null ? '—' : fmtMoney(r.earnedCents)}</td>
                  <td>{ago(t, r.lastActiveAt)}</td>
                  {me.role === 'manager' && me.showBalance && <td>{fmtMoney(r.clientCents)}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {dialogOpen && <AddDialog t={t} onClose={() => setDialogOpen(false)} onDone={load} />}
    </>
  );
}

// ── one-time cabinet credentials from the add batch ─────────────────────────

function AccountsList({ accounts, t }: { accounts: Array<{ email: string; password: string }>; t: T }) {
  const [copied, setCopied] = useState(false);
  // «логин пароль» строками — формат под пересылку списком (Тим, 08.08).
  const text = accounts.map((a) => `${a.email} ${a.password}`).join('\n');
  return (
    <div class="creds">
      <pre>{text}</pre>
      <div class="creds-actions">
        <button
          type="button"
          class="btn-primary"
          onClick={() => navigator.clipboard.writeText(text).then(() => setCopied(true))}
        >
          {copied ? t('pool.creds.copied') : t('pool.creds.copy', { n: accounts.length })}
        </button>
      </div>
      <div class="muted">{t('pool.creds.note')}</div>
    </div>
  );
}

// ── add-people dialog ────────────────────────────────────────────────────────

function AddDialog({ t, onClose, onDone }: { t: T; onClose: () => void; onDone: () => void }) {
  // The dialog must be opened with showModal(), not the `open` attribute: with
  // the attribute it renders non-modal — pinned to a corner, no backdrop,
  // clicks falling through to the table underneath.
  //
  // Opened from a callback ref rather than useEffect: the effect ran before
  // Preact had attached the ref, so the dialog stayed closed. The `!el.open`
  // guard matters because a callback ref fires on re-renders too, and
  // showModal() on an already-open dialog throws InvalidStateError.
  const openModal = (el: HTMLDialogElement | null) => {
    if (el && !el.open) el.showModal();
  };
  const [emailsRaw, setEmailsRaw] = useState('');
  const [rateRaw, setRateRaw] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<AddResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const emails = emailsRaw
    .split(/[\s,;]+/)
    .map((s) => s.trim())
    .filter((s) => s.includes('@'));

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const rate = rateRaw.trim() === '' ? undefined : Math.round(Number(rateRaw) * 100);
      const r = await api.addContributors({
        emails: emails.slice(0, 25),
        ...(rate != null && Number.isFinite(rate) ? { rateCents: rate } : {}),
      });
      setResult(r);
      onDone();
    } catch (e) {
      setError(rateErrorText(t, e) ?? String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <dialog ref={openModal} onClose={onClose}>
      <h3>{t('pool.dlg.title')}</h3>
      <label>{t('pool.dlg.emails')}</label>
      <textarea
        value={emailsRaw}
        onInput={(e) => setEmailsRaw((e.target as HTMLTextAreaElement).value)}
        placeholder={'one@example.com\ntwo@example.com'}
      />
      <label>{t('pool.dlg.rate')}</label>
      <input
        type="number"
        min="0"
        step="0.5"
        value={rateRaw}
        onInput={(e) => setRateRaw((e.target as HTMLInputElement).value)}
        placeholder={t('pool.dlg.ratePh')}
      />
      {emails.length > 25 && (
        <div class="result">{t('pool.dlg.over25')}</div>
      )}
      {result && (
        <div class="result">
          {t('pool.dlg.result', { created: result.created.length, existing: result.existing.length })}
        </div>
      )}
      {result && (result.foreign?.length ?? 0) > 0 && (
        <div class="strip warn">
          {/* Молчать здесь нельзя: человек добавил почту, съёмщик в пуле не
              появился, и без объяснения это выглядит как сбой. */}
          {t('pool.dlg.foreign')} {result.foreign.join(', ')}
        </div>
      )}
      {result && result.accounts.length > 0 && (
        <AccountsList accounts={result.accounts} t={t} />
      )}
      {error && (
        <div class="result">
          {t('pool.dlg.error')} {error}
        </div>
      )}
      <div class="actions">
        <button class="btn-ghost" onClick={onClose}>
          {result ? t('pool.dlg.close') : t('pool.dlg.cancel')}
        </button>
        {!result && (
          <button class="btn-primary" disabled={busy || emails.length === 0} onClick={submit}>
            {busy ? t('pool.dlg.adding') : t('pool.dlg.submit', { n: Math.min(emails.length, 25) })}
          </button>
        )}
      </div>
    </dialog>
  );
}
